import React, {
  useMemo, useState, useCallback, useEffect,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Divider,
  Popover,
  Table,
  Row,
  Col,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import OnTraccrButton from '../../../common/buttons/OnTraccrButton';

import CompanySettingsCard from '../../CompanySettingsCard';

import QuickbooksIntegration from './QuickbooksIntegration';
import QuickbooksIntegrationDrawer from './QuickBooksIntegationDrawer';
import QBAuditLogDrawer from './QBAuditLog/QBAuditLogDrawer';

import { getQuickbooksIntegrations } from '../../state/settings.actions';

import useToggle from '../../../common/hooks/useToggle';
import ErrorRecipientSelector from '../ErrorRecipientSelector';

import FormDetailView from '../../../forms/CompletedForms/FormDetailView';
import InvoiceAddDrawer from '../../../payables/invoices/InvoiceAddDrawer';
import TimeCardSlider from '../../../timecards/TimeCardSlider/TimeCardSlider';
import TimeTracking from '../../../state/timeTracking';
import { getFormById, clearFormSelection } from '../../../forms/state/forms.actions';
import { getTimecardHistory } from '../../../timecards/state/timecards.actions';
import { getInvoices } from '../../../payables/invoices/state/invoices.actions';
import { getIdMap } from '../../../helpers/helpers';
import { INVOICE_DRAWER_VIEW_MODE } from '../../../payables/invoices/invoiceConstants';

const onHeaderCell = () => ({
  style: {
    backgroundColor: 'white',
  },
});

function QuickBooks({
  visible,
}) {
  const dispatch = useDispatch();

  const divisions = useSelector((state) => state.settings.divisions);
  const integrations = useSelector((state) => state.settings.quickbookIntegrations);
  const users = useSelector((state) => state.users.users);
  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const timeCardHistory = useSelector((state) => state.timecards.history);

  const [addDrawerOpen, setAddDrawerOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState();

  const [showQbFormDetail, setShowQbFormDetail] = useState(false);
  const [showQbInvoiceDrawer, setShowQbInvoiceDrawer] = useState(false);
  const [qbSelectedInvoice, setQbSelectedInvoice] = useState({});
  const [showQbTimeSlider, setShowQbTimeSlider] = useState(false);
  const [qbTimeSelectedRow, setQbTimeSelectedRow] = useState();

  const {
    isToggled: logDrawerOpen,
    toggle: toggleLogDrawer,
  } = useToggle();

  const userMap = useMemo(() => getIdMap(users), [users]);

  const qbTimeEntries = useMemo(() => {
    if (!qbTimeSelectedRow?.id) return [];
    const list = timeEntryUserMap?.[qbTimeSelectedRow.userId] ?? [];
    const entry = list.find((t) => t.id === qbTimeSelectedRow.id);
    return entry ? [entry] : [];
  }, [timeEntryUserMap, qbTimeSelectedRow]);

  const qbTimeUser = useMemo(
    () => userMap[qbTimeSelectedRow?.userId] ?? {},
    [userMap, qbTimeSelectedRow],
  );

  const qbTimeHistory = useMemo(() => {
    if (!qbTimeSelectedRow?.id) return [];
    return timeCardHistory.filter((h) => h.taskId === qbTimeSelectedRow.id);
  }, [timeCardHistory, qbTimeSelectedRow]);

  const closeQbFormDetail = useCallback(() => {
    setShowQbFormDetail(false);
    dispatch(clearFormSelection());
  }, [dispatch]);

  const onQbAuditLinkedRow = useCallback(async (record) => {
    const { linkedEntity } = record;
    if (!linkedEntity?.type || !linkedEntity?.id) return;
    try {
      if (linkedEntity.type === 'form') {
        const data = await dispatch(getFormById(linkedEntity.id, { getRecentDraft: true }));
        if (data) setShowQbFormDetail(true);
        else message.warning('Could not load form');
      } else if (linkedEntity.type === 'invoice') {
        const list = await dispatch(getInvoices({ id: linkedEntity.id }));
        if (list?.length) {
          setQbSelectedInvoice(list[0]);
          setShowQbInvoiceDrawer(true);
        } else {
          message.warning('Could not load invoice');
        }
      } else if (linkedEntity.type === 'task') {
        if (!linkedEntity.userId) {
          message.warning('Time entry is missing user context');
          return;
        }
        await dispatch(TimeTracking.getTimeEntries({
          ids: [linkedEntity.id],
          userId: linkedEntity.userId,
        }));
        await dispatch(getTimecardHistory(linkedEntity.userId));
        setQbTimeSelectedRow({ id: linkedEntity.id, userId: linkedEntity.userId });
        setShowQbTimeSlider(true);
      }
    } catch (err) {
      message.error('Could not open linked record');
    }
  }, []);

  const closeAddDrawer = useCallback(() => {
    setAddDrawerOpen(false);
    setSelectedIntegration();
  }, []);
  const openAddDrawer = useCallback(() => setAddDrawerOpen(true), []);
  const onRowClick = useCallback((record) => {
    openAddDrawer();
    setSelectedIntegration(record);
  }, [openAddDrawer]);

  useEffect(() => {
    if (visible) {
      dispatch(getQuickbooksIntegrations());
    }
  }, [visible]);

  const columns = useMemo(() => [{
    title: 'Divisions',
    dataIndex: 'divisions',
    onHeaderCell,
    render: (divisionIds) => {
      const {
        names,
        excessCount,
        allNames = [],
      } = divisionIds?.reduce((acc, divId, index) => {
        const { [divId]: { name } = {} } = divisions;
        if (!name) return acc;
        acc.allNames.push(<p>{name}</p>);
        if (index > 2) {
          acc.excessCount += 1;
        } else {
          acc.names += `${index === 0 ? '' : ', '}${name}`;
        }
        return acc;
      }, { names: '', excessCount: 0, allNames: [] }) ?? {};
      if (excessCount === 0) return names;
      const suffix = excessCount === 1 ? '' : 's';
      const fullName = excessCount === 0
        ? names
        : `${names} and ${excessCount} other division${suffix}`;
      return (
        <Popover
          content={(
            <div>
              <span style={{ fontWeight: 'bold', fontSize: 18, color: 'black' }}>Divisions</span>
              <Divider style={{ margin: 0, marginBottom: 5 }} />
              {allNames}
            </div>
          )}
        >
          {fullName}
        </Popover>
      );
    },
  }], [divisions]);

  const isSingleDiv = useMemo(() => Object.keys(divisions).length <= 1, [divisions]);
  return (
    <>
      <CompanySettingsCard
        title="QuickBooks"
        leftHeaderView={(
          <Row justify="end" gutter={10}>
            <Col>
              {
                !integrations?.length
                  ? null
                  : (
                    <OnTraccrButton
                      title="Log"
                      type="primary"
                      onClick={toggleLogDrawer}
                    />
                  )
              }
            </Col>
            <Col>
              {
                isSingleDiv
                  ? null
                  : (
                    <OnTraccrButton
                      title="Add"
                      type="primary"
                      onClick={openAddDrawer}
                    />
                  )
              }
            </Col>
          </Row>
      )}
      >
        {
          isSingleDiv
            ? (
              <QuickbooksIntegration
                selectedIntegration={integrations?.[0]}
                visible={visible}
                isSingleDiv
              />
            ) : (
              <Table
                columns={columns}
                dataSource={integrations}
                pagination={false}
                size="small"
                scroll={{ y: 'calc(100vh - 220px', x: 'hidden' }}
                onRow={(record) => ({
                  onClick: () => onRowClick(record),
                })}
                rowClassName="quickbooks-integration-row"
              />
            )
        }
      </CompanySettingsCard>
      <CompanySettingsCard title="Settings">
        <ErrorRecipientSelector
          recipientsKey="qboErrorRecipients"
          integration="Quickbooks"
        />
      </CompanySettingsCard>
      <QuickbooksIntegrationDrawer
        visible={addDrawerOpen}
        onClose={closeAddDrawer}
        selectedIntegration={selectedIntegration}
      />
      <QBAuditLogDrawer
        visible={logDrawerOpen}
        onClose={toggleLogDrawer}
        onLinkedRowClick={onQbAuditLinkedRow}
      />
      <FormDetailView
        userMap={userMap}
        visible={showQbFormDetail}
        onClose={closeQbFormDetail}
        startOnFields
        shouldRedirect={false}
        zIndex={1400}
      />
      <InvoiceAddDrawer
        formId={qbSelectedInvoice?.formId}
        invoice={qbSelectedInvoice}
        visible={showQbInvoiceDrawer}
        mode={INVOICE_DRAWER_VIEW_MODE}
        useRange={false}
        closeDrawer={() => {
          setShowQbInvoiceDrawer(false);
          setQbSelectedInvoice({});
        }}
        updateMode={() => {}}
        divisionId={qbSelectedInvoice?.divisionId}
        zIndex={1400}
        hasWrite={false}
      />
      <TimeCardSlider
        visible={showQbTimeSlider}
        onClose={() => {
          setShowQbTimeSlider(false);
          setQbTimeSelectedRow();
        }}
        user={qbTimeUser}
        initialEntries={qbTimeEntries}
        initialHistory={qbTimeHistory}
        isDisplay
      />
    </>
  );
}

QuickBooks.propTypes = {
  visible: PropTypes.bool.isRequired,
};

export default QuickBooks;
